import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.js";

/**
 * The header search box — docs/01-PRODUCT-SPEC.md §9.2.
 *
 * Opened with `/` or `Cmd`/`Ctrl`+`K`, navigated with the arrow keys, and grouped programme → form
 * → cycle exactly as the API returns it. The dashboard does no ranking of its own: the order the
 * server sends is the order `ts_rank_cd` produced, and re-sorting it here would quietly undo the
 * one part of this feature that had to be done in SQL.
 */

interface SearchResult {
  submissionId: string;
  reference: string | null;
  respondentName: string | null;
  respondentEmail: string | null;
  submittedAt: string | null;
  reviewStatus: string;
  snippet: string;
}

interface SearchGroup {
  programme: string;
  form: { id: string; title: string };
  cycle: string | null;
  results: SearchResult[];
}

interface SearchResponse {
  query: string;
  took: number;
  totalMatches: number;
  groups: SearchGroup[];
  facets: {
    programmes: { id: string; label: string; count: number }[];
    cycles: { label: string; count: number }[];
    reviewStatuses: { status: string; count: number }[];
  };
}

export const GlobalSearch = ({
  onOpenSubmission,
}: {
  onOpenSubmission: (submissionId: string) => void;
}): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [cursor, setCursor] = useState(0);
  const [programmeFilter, setProgrammeFilter] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(term), 200);
    return () => clearTimeout(handle);
  }, [term]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;

      if (
        (event.key === "k" && (event.metaKey || event.ctrlKey)) ||
        (event.key === "/" && !typing)
      ) {
        event.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const query = useQuery({
    queryKey: ["global-search", debounced, programmeFilter],
    queryFn: () => {
      const params = new URLSearchParams({ q: debounced });
      if (programmeFilter) params.set("programmeId", programmeFilter);
      return apiGet<SearchResponse>(`/search?${params.toString()}`);
    },
    enabled: open && debounced.trim().length >= 2,
  });

  const flat = useMemo(
    () =>
      (query.data?.groups ?? []).flatMap((group) =>
        group.results.map((result) => ({ group, result })),
      ),
    [query.data],
  );

  useEffect(() => setCursor(0), [debounced, programmeFilter]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        aria-label="Search everything"
        className="flex items-center gap-2 rounded-md border p-2 text-sm text-muted-foreground sm:px-3 sm:py-1.5"
      >
        {/* On a phone the label and the keyboard hint are both dead weight — there is no `/` key
            in reach and the header has no room. The icon alone opens the same dialog. */}
        <Search aria-hidden className="h-4 w-4 sm:hidden" />
        <span className="hidden sm:inline">Search everything</span>
        <kbd className="hidden rounded border px-1 text-xs sm:inline">/</kbd>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/30 p-8"
      onClick={() => setOpen(false)}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search all forms and cycles"
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setCursor((index) => Math.min(index + 1, Math.max(flat.length - 1, 0)));
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              setCursor((index) => Math.max(index - 1, 0));
            }
            if (event.key === "Enter") {
              const hit = flat[cursor];
              if (hit) {
                onOpenSubmission(hit.result.submissionId);
                setOpen(false);
              }
            }
          }}
          placeholder="Name, email, phone or reference — across every form and cycle"
          className="border-b px-4 py-3 text-sm outline-none"
          aria-describedby="search-status"
        />

        <p
          id="search-status"
          role="status"
          className="border-b px-4 py-1 text-xs text-muted-foreground"
        >
          {query.isFetching
            ? "Searching…"
            : query.data
              ? `${query.data.totalMatches} result${query.data.totalMatches === 1 ? "" : "s"} in ${query.data.took} ms`
              : "Type at least two characters"}
        </p>

        {query.data && query.data.facets.programmes.length > 1 && (
          <div className="flex flex-wrap gap-1 border-b px-4 py-2 text-xs">
            <button
              type="button"
              onClick={() => setProgrammeFilter(undefined)}
              className={`rounded-full border px-2 py-0.5 ${!programmeFilter ? "bg-primary text-primary-foreground" : ""}`}
            >
              All programmes
            </button>
            {query.data.facets.programmes.map((facet) => (
              <button
                key={facet.id}
                type="button"
                onClick={() => setProgrammeFilter(facet.id)}
                className={`rounded-full border px-2 py-0.5 ${programmeFilter === facet.id ? "bg-primary text-primary-foreground" : ""}`}
              >
                {facet.label} ({facet.count})
              </button>
            ))}
          </div>
        )}

        <ul className="flex-1 overflow-auto py-1">
          {(query.data?.groups ?? []).map((group) => (
            <li key={`${group.form.id}-${group.cycle ?? ""}`}>
              <p className="bg-muted/50 px-4 py-1 text-xs font-medium text-muted-foreground">
                {group.programme} › {group.form.title}
                {group.cycle ? ` › ${group.cycle}` : ""}
              </p>
              <ul>
                {group.results.map((result) => {
                  const index = flat.findIndex(
                    (entry) => entry.result.submissionId === result.submissionId,
                  );
                  return (
                    <li key={result.submissionId}>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenSubmission(result.submissionId);
                          setOpen(false);
                        }}
                        aria-current={index === cursor ? "true" : undefined}
                        className={`block w-full px-4 py-2 text-left text-sm ${index === cursor ? "bg-accent/20" : "hover:bg-muted/50"}`}
                      >
                        <span className="flex justify-between gap-2">
                          <strong>{result.respondentName ?? result.reference ?? "—"}</strong>
                          <span className="text-xs text-muted-foreground">
                            {result.reviewStatus}
                          </span>
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {result.reference} · {result.respondentEmail ?? "—"}
                        </span>
                        {/* Safe: `buildSnippet` HTML-escapes the text and only then inserts its
                            own <mark> tags, so the sole markup here is the server's. It also masks
                            emails and phone numbers before highlighting, for a caller without
                            `view_pii` — see apps/api/src/services/submissionView.ts. */}
                        <span
                          className="block text-xs"
                          dangerouslySetInnerHTML={{ __html: result.snippet }}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
